import { useState, useEffect } from 'react';
import {
  Tabs,
  Table,
  TextInput,
  Select,
  Button,
  Group,
  Badge,
  Pagination,
  Card,
  Stack,
  Text,
  Loader,
  Alert,
  ActionIcon,
  Modal,
  Switch,
  Title
} from '@mantine/core';
import {
  IconSearch,
  IconEye,
  IconUsers,
  IconExclamationCircle,
  IconRefresh,
  IconCreditCard
} from '@tabler/icons-react';
import { api } from '../../services/api';
import SubscriptionManager from './SubscriptionManager';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [userType, setUserType] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [updatingUser, setUpdatingUser] = useState(false);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [subscriptionUserId, setSubscriptionUserId] = useState(null);
  const [subscriptionUsername, setSubscriptionUsername] = useState('');

  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchTerm, userType]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20'
      });
      
      if (searchTerm) params.append('search', searchTerm);
      if (userType) params.append('type', userType);

      const response = await api.getAdminUsers(Object.fromEntries(params));
      setUsers(response.users);
      setTotalPages(response.pagination.pages);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchUsers();
  };

  const handleUserTypeChange = (value) => {
    setUserType(value);
    setCurrentPage(1);
  };

  const viewUserDetails = async (userId) => {
    try {
      const response = await api.getAdminUser(userId);
      setSelectedUser(response.user);
      setUserModalOpen(true);
    } catch (err) {
      console.error('Error fetching user details:', err);
    }
  };

  const openSubscriptionManager = (userId, username) => {
    setSubscriptionUserId(userId);
    setSubscriptionUsername(username);
    setSubscriptionModalOpen(true);
  };

  const updateUserStatus = async (field, value) => {
    if (!selectedUser) return;

    try {
      setUpdatingUser(true);
      const updateData = { [field]: value };
      console.log('Updating user:', selectedUser.id, 'with data:', updateData);
      
      const response = await api.updateAdminUser(selectedUser.id, updateData);
      console.log('Update response:', response);
      
      setSelectedUser(response.user);
      
      // Refresh the users list
      fetchUsers();
      
      // Show success feedback
      alert(`Successfully updated ${field} to ${value}`);
    } catch (err) {
      console.error('Error updating user:', err);
      alert(`Failed to update user: ${err.message || 'Unknown error'}`);
    } finally {
      setUpdatingUser(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading && users.length === 0) {
    return (
      <Tabs.Panel value="users" pt="xs">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Loader size="lg" />
        </div>
      </Tabs.Panel>
    );
  }

  return (
    <Tabs.Panel value="users" pt="xs">
      <Card shadow="sm" padding="lg" radius="md" withBorder mb="md">
        <Stack gap="md">
          <Group align="center" gap="md">
            <TextInput
              placeholder="Search users..."
              leftSection={<IconSearch size="0.9rem" />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              style={{ flex: 1 }}
            />
            <Select
              placeholder="User type"
              value={userType}
              onChange={handleUserTypeChange}
              data={[
                { value: '', label: 'All Users' },
                { value: 'staff', label: 'Staff' },
                { value: 'seller', label: 'Sellers' },
                { value: 'regular', label: 'Regular Users' }
              ]}
              clearable
            />
            <Button onClick={handleSearch} loading={loading}>
              Search
            </Button>
            <ActionIcon onClick={fetchUsers} variant="light">
              <IconRefresh size="1rem" />
            </ActionIcon>
          </Group>

          {error && (
            <Alert icon={<IconExclamationCircle size="1rem" />} color="red">
              {error}
            </Alert>
          )}
        </Stack>
      </Card>

      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Username</Table.Th>
              <Table.Th>Email</Table.Th>
              <Table.Th>Name</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Joined</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {users.map((user) => (
              <Table.Tr key={user.id}>
                <Table.Td>
                  <Text fw={500}>{user.username}</Text>
                </Table.Td>
                <Table.Td>{user.email}</Table.Td>
                <Table.Td>
                  {user.first_name || user.last_name
                    ? `${user.first_name} ${user.last_name}`.trim()
                    : '—'
                  }
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    {!user.is_active && (
                      <Badge color="red" variant="light" size="sm">
                        Inactive
                      </Badge>
                    )}
                    {user.is_superuser && (
                      <Badge color="purple" variant="light" size="sm">
                        Super Admin
                      </Badge>
                    )}
                    {user.is_staff && !user.is_superuser && (
                      <Badge color="blue" variant="light" size="sm">
                        Staff
                      </Badge>
                    )}
                    {user.is_seller && (
                      <Badge color="green" variant="light" size="sm">
                        Seller
                      </Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  {user.is_superuser ? 'Super Admin' :
                   user.is_staff ? 'Staff' :
                   user.is_seller ? 'Seller' : 'User'}
                </Table.Td>
                <Table.Td>{formatDate(user.date_joined)}</Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <ActionIcon 
                      variant="light" 
                      onClick={() => viewUserDetails(user.id)}
                      title="View Details"
                    >
                      <IconEye size="1rem" />
                    </ActionIcon>
                    <ActionIcon 
                      variant="light" 
                      color="green"
                      onClick={() => openSubscriptionManager(user.id, user.username)}
                      title="Manage Subscription"
                    >
                      <IconCreditCard size="1rem" />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        {users.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <Text c="dimmed">No users found</Text>
          </div>
        )}

        {totalPages > 1 && (
          <Group align="center" mt="md">
            <Pagination
              value={currentPage}
              onChange={setCurrentPage}
              total={totalPages}
            />
          </Group>
        )}
      </Card>

      {/* User Details Modal */}
      <Modal
        opened={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        title={<Title order={3}>User Details</Title>}
        size="md"
      >
        {selectedUser && (
          <Stack gap="md">
            <Group align="apart">
              <Text fw={500}>Username:</Text>
              <Text>{selectedUser.username}</Text>
            </Group>
            
            <Group align="apart">
              <Text fw={500}>Email:</Text>
              <Text>{selectedUser.email}</Text>
            </Group>
            
            <Group align="apart">
              <Text fw={500}>Full Name:</Text>
              <Text>
                {selectedUser.first_name || selectedUser.last_name
                  ? `${selectedUser.first_name} ${selectedUser.last_name}`.trim()
                  : '—'
                }
              </Text>
            </Group>
            
            <Group align="apart">
              <Text fw={500}>Date Joined:</Text>
              <Text>{formatDate(selectedUser.date_joined)}</Text>
            </Group>
            
            <Group align="apart">
              <Text fw={500}>Last Login:</Text>
              <Text>{formatDate(selectedUser.last_login)}</Text>
            </Group>

            {/* Admin Controls */}
            <Card withBorder p="md" mt="md">
              <Title order={4} mb="md">Admin Controls</Title>
              
              <Stack gap="md">
                <Group align="apart">
                  <Text>Active Status</Text>
                  <Switch
                    checked={selectedUser.is_active}
                    onChange={(e) => updateUserStatus('is_active', e.currentTarget.checked)}
                    disabled={updatingUser}
                  />
                </Group>
                
                <Group align="apart">
                  <Text>Staff Status</Text>
                  <Switch
                    checked={selectedUser.is_staff}
                    onChange={(e) => updateUserStatus('is_staff', e.currentTarget.checked)}
                    disabled={updatingUser}
                  />
                </Group>
              </Stack>
            </Card>

            {/* Seller Information */}
            {selectedUser.seller_profile && (
              <Card withBorder p="md" mt="md">
                <Title order={4} mb="md">Seller Information</Title>
                <Stack gap="sm">
                  <Group align="apart">
                    <Text fw={500}>Business Name:</Text>
                    <Text>{selectedUser.seller_profile.business_name}</Text>
                  </Group>
                  <Group align="apart">
                    <Text fw={500}>Status:</Text>
                    <Badge color={
                      selectedUser.seller_profile.seller_status === 'approved' ? 'green' :
                      selectedUser.seller_profile.seller_status === 'pending' ? 'yellow' : 'red'
                    }>
                      {selectedUser.seller_profile.seller_status}
                    </Badge>
                  </Group>
                  <Group align="apart">
                    <Text fw={500}>Verified:</Text>
                    <Badge color={selectedUser.seller_profile.is_verified ? 'green' : 'gray'}>
                      {selectedUser.seller_profile.is_verified ? 'Yes' : 'No'}
                    </Badge>
                  </Group>
                </Stack>
              </Card>
            )}
          </Stack>
        )}
      </Modal>

      {/* Subscription Manager Modal */}
      <SubscriptionManager
        opened={subscriptionModalOpen}
        onClose={() => setSubscriptionModalOpen(false)}
        userId={subscriptionUserId}
        username={subscriptionUsername}
      />
    </Tabs.Panel>
  );
};

export default UserManagement;